import { DateTime } from 'luxon';
import { useState } from 'react';
import { Button } from '../../components/button';
import { Card, CardBody, CardHeader, Divider, Muted, Stack, Strong } from '../../components/ui';
import { useWorkerReviewsQuery } from '../../services/serviceService/__generated__/queries.generated';
import { averageLabel } from '../StayBookingPage/stayLifecycle';

const PAGE = 5;

const CATEGORY_LABELS: [key: string, label: string][] = [
  ['thoroughness', 'Thoroughness'],
  ['punctuality', 'On time'],
  ['communication', 'Communication'],
  ['photoCompleteness', 'Photos'],
];

/**
 * Customers' reviews of a worker (spec 019 W12, R16) from `getCarRatings` on
 * the worker's `carId`. Only reviews that show are returned (both sides in,
 * or 14 days past).
 */
export function WorkerReviewsCard({ workerCarId }: { workerCarId: string }) {
  const [limit, setLimit] = useState(PAGE);
  const { data, loading } = useWorkerReviewsQuery({
    variables: { carId: workerCarId, limit, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const result = data?.getCarRatings;
  const reviews = (result?.reviews ?? []).filter(r => r.rating.serviceCustomerRating);
  if (!result && loading) return null;
  const summary = result?.summary;

  return (
    <Card data-testid="worker-reviews">
      <CardHeader
        title={
          summary?.totalRatings
            ? `★ ${averageLabel(summary.averageRating)} · ${summary.totalRatings} review${summary.totalRatings === 1 ? '' : 's'}`
            : 'Reviews'
        }
      />
      <CardBody>
        {reviews.length === 0 ? (
          <Muted $size="sm">No reviews yet.</Muted>
        ) : (
          <Stack $gap={4}>
            {reviews.map((r, i) => {
              const s = r.rating.serviceCustomerRating!;
              return (
                <Stack key={r.rating.id} $gap={4}>
                  {i > 0 && <Divider />}
                  <div>
                    <Strong>
                      {r.guestName} · {s.overall} of 5
                    </Strong>
                    <Muted $size="sm" style={{ display: 'block' }}>
                      {DateTime.fromISO(String(r.tripDate)).toFormat('LLLL yyyy')} ·{' '}
                      {CATEGORY_LABELS.map(([key, label]) => `${label} ${(s as any)[key]}`).join(
                        ', ',
                      )}
                    </Muted>
                  </div>
                  {s.review && <Muted>“{s.review}”</Muted>}
                </Stack>
              );
            })}
            {(result?.totalCount ?? 0) > limit && (
              <div>
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Show more reviews"
                  isLoading={loading}
                  onClick={() => setLimit(l => l + PAGE)}
                />
              </div>
            )}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}
